import type { AxiosError } from 'axios';
import api from './api';
import { reportError } from '../lib/errorManager';
import { wrapApiCall, wrapDirectApiCall } from '../utils/apiWrapper';
import type { ApiResponse, PaidStatusResponse } from '../types/api.types';

export interface CreateGuestSessionResult {
  ok: boolean;
  status: number;
  emailConflict: boolean;
  email?: string | null;
  name?: string | null;
  message?: string;
}

interface GuestSessionResponse {
  status: string;
  data: {
    user: {
      id: number;
      name: string;
      email: string | null;
      provider: string;
    };
  };
}

export interface EmailSignupRequest {
  name: string;
  email: string;
  password: string;
}

export interface EmailLoginRequest {
  email: string;
  password: string;
}

export interface AuthUser {
  loginId: string;
  name: string;
  [key: string]: any;
}

export interface ForgotPasswordRequest { email: string; }
export interface ResetPasswordRequest { token: string; password: string; }
export interface MessageResponse { status: string; message: string; }
export interface EmailVerificationResult { emailVerified: boolean }

class AuthService {
  async signup(data: EmailSignupRequest): Promise<ApiResponse<AuthUser>> {
    return wrapApiCall<AuthUser, { user: AuthUser }>(
      () => api.post<{ status: string; data: { user: AuthUser } }>('/v1/auth/signup', data),
      'Failed to sign up',
      (responseData) => responseData.user
    );
  }

  async login(data: EmailLoginRequest): Promise<ApiResponse<AuthUser>> {
    return wrapApiCall<AuthUser, { user: AuthUser }>(
      () => api.post<{ status: string; data: { user: AuthUser } }>('/v1/auth/login', data),
      'Failed to sign in',
      (responseData) => responseData.user
    );
  }

  async forgotPassword(data: ForgotPasswordRequest): Promise<ApiResponse<MessageResponse>> {
    return wrapDirectApiCall<MessageResponse>(
      () => api.post<MessageResponse>('/v1/auth/forgot-password', data),
      'Failed to send reset email'
    );
  }

  async resetPassword(data: ResetPasswordRequest): Promise<ApiResponse<MessageResponse>> {
    return wrapDirectApiCall<MessageResponse>(
      () => api.post<MessageResponse>('/v1/auth/reset-password', data),
      'Failed to reset password'
    );
  }

  /** Request (or re-send) the 4-digit email-verification OTP for the logged-in user. */
  async sendEmailOtp(): Promise<ApiResponse<EmailVerificationResult>> {
    return wrapApiCall<EmailVerificationResult>(
      () => api.post<{ status: string; data: EmailVerificationResult }>('/v1/auth/send-email-otp'),
      'Failed to send verification code'
    );
  }

  /** Submit the 4-digit OTP to verify the logged-in user's email. */
  async verifyEmailOtp(otp: string): Promise<ApiResponse<EmailVerificationResult>> {
    return wrapApiCall<EmailVerificationResult>(
      () => api.post<{ status: string; data: EmailVerificationResult }>('/v1/auth/verify-email-otp', { otp }),
      'Failed to verify email'
    );
  }

  async updateTier(tier: 'paid' | 'free'): Promise<ApiResponse<PaidStatusResponse>> {
    return wrapApiCall<PaidStatusResponse>(
      () => api.patch<{ status: string; data: PaidStatusResponse }>('/v1/auth/tier', { tier }),
      'Failed to update subscription tier'
    );
  }

  async createGuestSession(opts?: { email?: string; name?: string }): Promise<CreateGuestSessionResult> {
    const body: { email?: string; name?: string } = {};
    if (opts?.email) body.email = opts.email;
    if (opts?.name) body.name = opts.name;
    const payload = Object.keys(body).length > 0 ? body : undefined;
    try {
      const response = await api.post<GuestSessionResponse>('/v1/auth/guest', payload, { timeout: 12000 });
      const user = response.data?.data?.user;
      return {
        ok: true,
        status: 201,
        emailConflict: false,
        email: user?.email ?? null,
        name: user?.name ?? null,
      };
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string; message?: string }>;
      const status = axiosError.response?.status ?? 0;
      const message =
        axiosError.response?.data?.error ??
        axiosError.response?.data?.message ??
        (error instanceof Error ? error.message : 'Failed to create guest session');
      const emailConflict = status === 409;

      // 4xx are expected app flow (validation, conflict). Only report unexpected failures.
      if (status === 0 || status >= 500) {
        reportError(error, {
          source: 'authService',
          operation: 'createGuestSession',
          status,
          message,
        });
      }

      return { ok: false, status, emailConflict, message };
    }
  }

}

export const authService = new AuthService();
